import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const EDIT_SECTIONS = [
    { title: 'General', fields: [
        ['companyName', 'Company Name'], ['industry', 'Industry'], ['website', 'Website'], ['registrationNumber', 'Registration Number']
    ]},
    { title: 'Contact', fields: [
        ['primaryEmail', 'Primary Email'], ['primaryPhone', 'Primary Phone'],
        ['contactPersonName', 'Contact Person Name'], ['contactPersonPhone', 'Contact Person Phone'],
        ['emergencyContactName', 'Emergency Contact Name'], ['emergencyPhone', 'Emergency Phone'],
        ['supportEmail', 'Support Email'], ['supportPhone', 'Support Phone']
    ]},
    { title: 'Address', fields: [
        ['streetAddress', 'Street Address'], ['city', 'City'], ['stateProvince', 'State / Province'], ['postalCode', 'Postal Code'], ['country', 'Country']
    ]},
    { title: 'Operations', fields: [
        ['timezone', 'Timezone'], ['fleetSize', 'Fleet Size', 'number'], ['numberOfDrivers', 'Number of Drivers', 'number'],
        ['defaultShiftStart', 'Default Shift Start', 'time'], ['defaultShiftEnd', 'Default Shift End', 'time'],
        ['primaryServiceType', 'Primary Service Types (comma separated)']
    ]},
    { title: 'Financials & Tax', fields: [
        ['taxId', 'Tax ID'], ['vatPercentage', 'VAT %', 'number'], ['fiscalYearStart', 'Fiscal Year Start'], ['defaultCurrency', 'Default Currency']
    ]},
    { title: 'Bank Details', fields: [
        ['bankName', 'Bank Name'], ['accountHolderName', 'Account Holder'], ['accountName', 'Account Name'],
        ['accountNumber', 'Account Number'], ['iban', 'IBAN'], ['swiftCode', 'SWIFT Code']
    ]}
];

export default function OperatorProfile({ user, operatorDetails, onProfileUpdated }) {
    const [subscription, setSubscription] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState({});
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [logoBroken, setLogoBroken] = useState(false);

    useEffect(() => { setLogoBroken(false); }, [operatorDetails?.companyLogo]);

    const openEdit = () => {
        const f = {};
        EDIT_SECTIONS.forEach(sec => sec.fields.forEach(([key]) => {
            const v = operatorDetails?.[key];
            f[key] = Array.isArray(v) ? v.join(', ') : (v ?? '');
        }));
        setForm(f);
        setLogoFile(null);
        setLogoPreview(operatorDetails?.companyLogo ? `${API_URL}/uploads/${operatorDetails.companyLogo}` : null);
        setSaveError('');
        setIsEditing(true);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setSaveError('Logo must be an image file.'); return; }
        if (file.size > 5 * 1024 * 1024) { setSaveError('Logo must be under 5 MB.'); return; }
        setSaveError('');
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const handleSave = async () => {
        if (!form.companyName || !form.companyName.trim()) { setSaveError('Company name is required.'); return; }
        setSaving(true);
        setSaveError('');
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === 'string' ? v.trim() : v));
            if (logoFile) fd.append('companyLogo', logoFile);
            const res = await fetch(`${API_URL}/operators/${operatorDetails._id}`, { method: 'PUT', body: fd });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Failed to update profile');
            if (onProfileUpdated && data.operator) onProfileUpdated(data.operator);
            setIsEditing(false);
        } catch (err) {
            setSaveError(err.message || 'Network error while saving.');
        } finally {
            setSaving(false);
        }
    };
    const [loading, setLoading] = useState(true);

    const [countries, setCountries] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            if (!operatorDetails || !operatorDetails._id) return;
            try {
                const [subRes, countryRes] = await Promise.all([
                    fetch(`${API_URL}/subscriptions/operator/${operatorDetails._id}`),
                    fetch(`${API_URL}/countries`)
                ]);
                
                if (subRes.ok) {
                    const data = await subRes.json();
                    if (data && data.length > 0) {
                        setSubscription(data[0]);
                    }
                }
                
                if (countryRes.ok) {
                    const cData = await countryRes.json();
                    setCountries(cData);
                }
            } catch (err) {
                console.error("Failed to fetch details:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [operatorDetails]);

    if (!operatorDetails) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#6b7280', padding: 40 }}>
                Loading Profile...
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const InfoRow = ({ label, value }) => (
        <div className="info-row">
            <span className="info-label">{label}</span>
            <span className="info-value">{value || <span style={{color: '#aaa', fontStyle: 'italic'}}>N/A</span>}</span>
        </div>
    );

    let displayCurrency = operatorDetails.defaultCurrency || 'USD';
    let displayAmount = subscription?.amount || 0;

    if (subscription && subscription.planId && operatorDetails.country) {
        const countryObj = countries.find(c => c.name === operatorDetails.country);
        if (countryObj) {
            displayCurrency = countryObj.currency || displayCurrency;
            const specificPriceObj = subscription.planId.prices?.find(p => p.country === operatorDetails.country);
            if (specificPriceObj && specificPriceObj.amount != null) {
                displayAmount = specificPriceObj.amount;
            } else {
                const rate = countryObj.exchangeRate || countryObj.rate || 1;
                displayAmount = (subscription.planId.price || 0) * rate;
            }
        }
    }

    return (
        <div className="profile-wrapper">
            <style>{`
                .profile-wrapper {
                    padding: 30px;
                    max-width: 1400px;
                    margin: 0 auto;
                    font-family: 'Inter', system-ui, -apple-system, sans-serif;
                    animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                    background: transparent;
                    color: #334155;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.98); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                .hero-section {
                    position: relative;
                    border-radius: 24px;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    padding: 40px;
                    color: #0f172a;
                    display: flex;
                    align-items: center;
                    gap: 40px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
                    margin-bottom: 40px;
                    overflow: hidden;
                    border: 1px solid #cbd5e1;
                }
                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: -50%; left: -50%; width: 200%; height: 200%;
                    background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 50%);
                    animation: pulseGlow 10s infinite alternate linear;
                    pointer-events: none;
                }
                @keyframes pulseGlow {
                    0% { transform: scale(1) translate(0, 0); }
                    100% { transform: scale(1.2) translate(5%, 5%); }
                }
                .hero-logo-container {
                    position: relative;
                    z-index: 1;
                    width: 140px;
                    height: 140px;
                    border-radius: 50%;
                    background: #ffffff;
                    border: 2px solid rgba(16, 185, 129, 0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 54px;
                    font-weight: 800;
                    color: #10b981;
                    overflow: hidden;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
                }
                .hero-logo-container img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .hero-content {
                    position: relative;
                    z-index: 1;
                    flex: 1;
                }
                .hero-content h1 {
                    margin: 0 0 16px 0;
                    font-size: 46px;
                    font-weight: 800;
                    letter-spacing: -1px;
                    color: #0f172a;
                    text-shadow: none;
                }
                .hero-badges {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .badge {
                    padding: 8px 18px;
                    border-radius: 30px;
                    font-size: 13px;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                    background: rgba(99, 102, 241, 0.1);
                    border: 1px solid rgba(99, 102, 241, 0.2);
                    color: #16539C;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .badge:hover {
                    background: rgba(99, 102, 241, 0.15);
                }
                .badge-active {
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                    border-color: rgba(16, 185, 129, 0.3);
                }
                .badge-active:hover {
                    background: rgba(16, 185, 129, 0.15);
                }
                .cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
                    gap: 30px;
                }
                .profile-card {
                    background: #f8fafc;
                    border-radius: 20px;
                    padding: 32px;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    position: relative;
                    overflow: hidden;
                    color: #1e293b;
                }
                .profile-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(99, 102, 241, 0.2);
                    border-color: transparent;
                }
                .card-header {
                    margin-bottom: 28px;
                    border-bottom: 1px solid #e2e8f0;
                    padding-bottom: 16px;
                }
                .card-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    letter-spacing: 0.5px;
                }
                .card-title::before {
                    content: '';
                    display: inline-block;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: #10b981;
                }
                .info-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 14px;
                    padding: 12px 16px;
                    border-radius: 12px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    transition: background 0.2s;
                }
                .info-row:hover {
                    background: #f1f5f9;
                    border-color: #cbd5e1;
                }
                .info-label {
                    color: #64748b;
                    font-weight: 600;
                    flex: 1;
                    min-width: 140px;
                }
                .info-value {
                    color: #0f172a;
                    font-weight: 600;
                    flex: 1.5;
                    text-align: right;
                    word-break: break-word;
                }
                .status-pill {
                    padding: 6px 14px;
                    border-radius: 30px;
                    font-size: 12px;
                    font-weight: 800;
                    display: inline-block;
                    letter-spacing: 0.5px;
                    box-shadow: 0 0 15px rgba(16, 185, 129, 0.1);
                }
                .status-active { background: rgba(16, 185, 129, 0.1); color: #059669; border: 1px solid rgba(16,185,129,0.2); }
                .status-inactive { background: rgba(239, 68, 68, 0.1); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
                
                .edit-profile-btn {
                    position: relative; z-index: 1; align-self: flex-start;
                    background: #10b981; color: #fff; border: none; border-radius: 12px;
                    padding: 10px 20px; font-weight: 700; font-size: 14px; cursor: pointer;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.25); transition: background 0.2s;
                }
                .edit-profile-btn:hover { background: #059669; }
                .ep-overlay {
                    position: fixed; inset: 0; background: rgba(15,23,42,0.45); z-index: 1000;
                    display: flex; align-items: center; justify-content: center; padding: 20px;
                }
                .ep-modal {
                    background: #fff; border-radius: 20px; width: 100%; max-width: 860px; max-height: 90vh;
                    display: flex; flex-direction: column; box-shadow: 0 25px 50px rgba(0,0,0,0.2);
                }
                .ep-head, .ep-foot { padding: 20px 28px; display: flex; align-items: center; justify-content: space-between; }
                .ep-head { border-bottom: 1px solid #e2e8f0; }
                .ep-head h2 { margin: 0; font-size: 20px; color: #0f172a; }
                .ep-foot { border-top: 1px solid #e2e8f0; justify-content: flex-end; gap: 10px; }
                .ep-body { padding: 24px 28px; overflow-y: auto; }
                .ep-section-title { font-size: 12px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; margin: 22px 0 12px; }
                .ep-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px; }
                .ep-field label { display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; }
                .ep-field input { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 14px; color: #0f172a; background: #fff; }
                .ep-field input:focus { outline: none; border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }
                .ep-logo-row { display: flex; align-items: center; gap: 20px; }
                .ep-logo-preview { width: 88px; height: 88px; border-radius: 50%; border: 2px dashed #cbd5e1; display: flex; align-items: center; justify-content: center; overflow: hidden; color: #94a3b8; font-size: 12px; background: #f8fafc; }
                .ep-logo-preview img { width: 100%; height: 100%; object-fit: cover; }
                .ep-btn { border: none; border-radius: 10px; padding: 10px 18px; font-weight: 700; font-size: 14px; cursor: pointer; }
                .ep-btn-secondary { background: #f1f5f9; color: #475569; }
                .ep-btn-primary { background: #10b981; color: #fff; }
                .ep-btn:disabled { opacity: 0.6; cursor: not-allowed; }
                .ep-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; padding: 10px 14px; border-radius: 10px; font-size: 13px; margin-bottom: 8px; }

                @media (max-width: 768px) {
                    .edit-profile-btn { align-self: center; }
                    .hero-section {
                        flex-direction: column;
                        text-align: center;
                        padding: 30px 20px;
                    }
                    .hero-badges {
                        justify-content: center;
                    }
                    .info-row {
                        flex-direction: column;
                        gap: 8px;
                        align-items: flex-start;
                    }
                    .info-value {
                        text-align: left;
                    }
                    .cards-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>

            {/* Hero Section */}
            <div className="hero-section">
                <div className="hero-logo-container">
                    {operatorDetails.companyLogo && !logoBroken ? (
                        <img src={`${API_URL}/uploads/${operatorDetails.companyLogo}`} alt="" onError={() => setLogoBroken(true)} />
                    ) : (
                        operatorDetails.companyName?.charAt(0) || 'O'
                    )}
                </div>
                <div className="hero-content">
                    <h1>{operatorDetails.companyName}</h1>
                    <div className="hero-badges">
                        {operatorDetails.industry && <span className="badge">{operatorDetails.industry}</span>}
                        {operatorDetails.status && (
                            <span className={`badge ${operatorDetails.status === 'Active' || operatorDetails.status === 'Approved' ? 'badge-active' : ''}`}>
                                {operatorDetails.status}
                            </span>
                        )}
                        {operatorDetails.website && (
                            <a href={operatorDetails.website.startsWith('http') ? operatorDetails.website : `https://${operatorDetails.website}`} target="_blank" rel="noreferrer" className="badge" style={{textDecoration: 'none'}}>
                                🌐 {operatorDetails.website}
                            </a>
                        )}
                    </div>
                </div>
                <button className="edit-profile-btn" onClick={openEdit}>✎ Edit Profile</button>
            </div>

            <div className="cards-grid">
                
                {/* Active Subscription */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Active Subscription</h3>
                    </div>
                    {loading ? (
                        <div style={{color: '#6b7280', fontSize: 14}}>Loading subscription details...</div>
                    ) : subscription ? (
                        <div className="info-list">
                            <InfoRow label="Plan Name" value={subscription.planId?.name || 'Unknown Plan'} />
                            <div className="info-row">
                                <span className="info-label">Status</span>
                                <span className="info-value">
                                    <span className={`status-pill ${subscription.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                                        {subscription.status}
                                    </span>
                                </span>
                            </div>
                            <InfoRow label="Billing Amount" value={`${displayCurrency} ${Number(displayAmount).toLocaleString()}`} />
                            <InfoRow label="Start Date" value={formatDate(subscription.startDate)} />
                            <InfoRow label="Next Billing" value={formatDate(subscription.nextBillingDate)} />
                            {subscription.planId && (
                                <>
                                    <div style={{marginTop: 15, marginBottom: 10, borderTop: '1px dashed #cbd5e1', paddingTop: 15}}>
                                        <h4 style={{fontSize: 13, margin: '0 0 10px 0', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5}}>Plan Limits</h4>
                                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Fleet: {subscription.planId.fleetLimit === -1 ? 'Unlimited' : subscription.planId.fleetLimit}
                                            </span>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Drivers: {subscription.planId.driversLimit === -1 ? 'Unlimited' : subscription.planId.driversLimit}
                                            </span>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Routes: {subscription.planId.routesLimit === -1 ? 'Unlimited' : subscription.planId.routesLimit}
                                            </span>
                                            <span className="badge" style={{background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: 12}}>
                                                Users: {subscription.planId.usersLimit === -1 ? 'Unlimited' : subscription.planId.usersLimit}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div style={{color: '#6b7280', fontStyle: 'italic', padding: '20px 0', textAlign: 'center', fontSize: 14}}>No active subscription found.</div>
                    )}
                </div>

                {/* Contact Information */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Contact Information</h3>
                    </div>
                    <div className="info-list">
                        <InfoRow label="Primary Email" value={operatorDetails.primaryEmail} />
                        <InfoRow label="Primary Phone" value={operatorDetails.primaryPhone} />
                        <InfoRow label="Contact Person" value={`${operatorDetails.contactPersonName || 'N/A'} ${operatorDetails.contactPersonPhone ? `(${operatorDetails.contactPersonPhone})` : ''}`} />
                        <InfoRow label="Emergency Contact" value={`${operatorDetails.emergencyContactName || 'N/A'} ${operatorDetails.emergencyPhone ? `(${operatorDetails.emergencyPhone})` : ''}`} />
                        <InfoRow label="Address" value={[operatorDetails.streetAddress, operatorDetails.city, operatorDetails.stateProvince, operatorDetails.postalCode, operatorDetails.country].filter(Boolean).join(', ')} />
                    </div>
                </div>

                {/* Operations & Logistics */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Operations & Logistics</h3>
                    </div>
                    <div className="info-list">
                        <InfoRow label="Timezone" value={operatorDetails.timezone} />
                        <InfoRow label="Fleet Size" value={operatorDetails.fleetSize ? `${operatorDetails.fleetSize} vehicles` : null} />
                        <InfoRow label="Drivers Count" value={operatorDetails.numberOfDrivers ? `${operatorDetails.numberOfDrivers} drivers` : null} />
                        <InfoRow label="Default Shifts" value={(operatorDetails.defaultShiftStart && operatorDetails.defaultShiftEnd) ? `${operatorDetails.defaultShiftStart} to ${operatorDetails.defaultShiftEnd}` : null} />
                        <InfoRow label="Primary Service Type" value={Array.isArray(operatorDetails.primaryServiceType) ? operatorDetails.primaryServiceType.join(', ') : operatorDetails.primaryServiceType} />
                    </div>
                </div>

                {/* Financials & Tax */}
                <div className="profile-card">
                    <div className="card-header">
                        <h3 className="card-title">Financials & Tax</h3>
                    </div>
                    <div className="info-list">
                        <InfoRow label="Registration Number" value={operatorDetails.registrationNumber} />
                        <InfoRow label="Tax ID" value={operatorDetails.taxId} />
                        <InfoRow label="VAT Percentage" value={operatorDetails.vatPercentage ? `${operatorDetails.vatPercentage}%` : null} />
                        <InfoRow label="Fiscal Year Start" value={operatorDetails.fiscalYearStart} />
                        <InfoRow label="Default Currency" value={operatorDetails.defaultCurrency} />
                    </div>
                </div>

                {/* Bank Details */}
                <div className="profile-card" style={{gridColumn: '1 / -1'}}>
                    <div className="card-header">
                        <h3 className="card-title">Bank Details</h3>
                    </div>
                    <div className="info-list" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px 60px'}}>
                        <InfoRow label="Bank Name" value={operatorDetails.bankName} />
                        <InfoRow label="Account Holder" value={operatorDetails.accountHolderName} />
                        <InfoRow label="Account Name" value={operatorDetails.accountName} />
                        <InfoRow label="Account Number" value={operatorDetails.accountNumber} />
                        <InfoRow label="IBAN" value={operatorDetails.iban} />
                        <InfoRow label="SWIFT Code" value={operatorDetails.swiftCode} />
                    </div>
                </div>

            </div>
            {isEditing && (
                <div className="ep-overlay" onClick={() => !saving && setIsEditing(false)}>
                    <div className="ep-modal" onClick={e => e.stopPropagation()}>
                        <div className="ep-head">
                            <h2>Edit Company Profile</h2>
                            <button className="ep-btn ep-btn-secondary" onClick={() => setIsEditing(false)} disabled={saving}>✕</button>
                        </div>
                        <div className="ep-body">
                            {saveError && <div className="ep-error">{saveError}</div>}
                            <div className="ep-section-title" style={{marginTop: 0}}>Company Logo</div>
                            <div className="ep-logo-row">
                                <div className="ep-logo-preview">
                                    {logoPreview ? <img src={logoPreview} alt="" onError={() => setLogoPreview(null)} /> : 'No logo'}
                                </div>
                                <div>
                                    <label className="ep-btn ep-btn-secondary" style={{display: 'inline-block'}}>
                                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                                        <input type="file" accept="image/*" onChange={handleLogoChange} style={{display: 'none'}} />
                                    </label>
                                    <div style={{fontSize: 12, color: '#94a3b8', marginTop: 6}}>PNG or JPG, square works best, max 5 MB</div>
                                </div>
                            </div>
                            {EDIT_SECTIONS.map(sec => (
                                <div key={sec.title}>
                                    <div className="ep-section-title">{sec.title}</div>
                                    <div className="ep-grid">
                                        {sec.fields.map(([key, label, type]) => (
                                            <div className="ep-field" key={key}>
                                                <label>{label}</label>
                                                <input
                                                    type={type || 'text'}
                                                    value={form[key] ?? ''}
                                                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="ep-foot">
                            <button className="ep-btn ep-btn-secondary" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</button>
                            <button className="ep-btn ep-btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
